import React from 'react';
import styled from 'styled-components';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import StatusBox from '@ui-parts/StatusBox';
import UserCategoryLabel from '@ui-elements/UserCategoryLabel';
import { useNavigate } from 'react-router-dom';
import { PALETTE, FONT_SIZES, JUDGEMENT_STATUS_COLORS } from '~/assets/styles/base';
import { ROUTES } from '~/constants/routes';
import { USER_CATEGORY, JUDGEMENT_STATUS, UserCategory, SurveyStatus } from '~/common/constants';
import {
  SURVEY_STATUSES,
  ANTISOCIAL_CHECK_NAME,
  ANTISOCIAL_CHECK_APPROVAL_NAME,
  CREDIT_CHECK_NAME,
} from '~/constants';
import { mixinCircle } from '~/assets/styles/mixin';

const StyledListItem = styled(ListItem)`
  margin: 0 0 27px 0;
  width: 100%;
  max-width: 960px;
  min-height: 139px;
  background-color: ${PALETTE.white};
  box-shadow: 2px 2px 4px #959fa815;
  border: 1px solid ${PALETTE.gray[100]};
  padding: 20px 24px 12px;
  box-sizing: border-box;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  transition: opacity 0.4s;
  cursor: pointer;
  position: relative;
  word-break: break-all;
  font-size: ${FONT_SIZES.body1};
  &:hover {
    opacity: 0.6;
  }
  // Arrow
  &:after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    right: 60px;
    margin: auto;
    width: 8px;
    height: 8px;
    border-top: solid 2px #a3b1be;
    border-right: solid 2px #a3b1be;
    transform: rotate(45deg);
  }
  //  Circus
  &:before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    margin: auto;
    right: 48px;
    ${mixinCircle}
    margin-left: 26px;
  }
`;

type ListItemInfoProps = {
  $isAdmin: boolean;
};
const ListItemInfo = styled.div<ListItemInfoProps>`
  font-size: ${FONT_SIZES.body2};
  margin-top: ${({ $isAdmin }) => ($isAdmin ? '16px' : '24px')};
`;

const QuestionNum = styled.span`
  font-size: ${FONT_SIZES.body1};
  margin: 0 60px 0 19px;
`;

const Id = styled.span`
  margin: 0 26px 0 4px;
`;

const CreatedDate = styled.span`
  margin: 0 26px 0 4px;
`;

const Modified = styled.span`
  margin: 0 26px 0 4px;
`;

const StatusBoxContainer = styled.div`
  position: absolute;
  top: 0;
`;

const JudgementStatuses = styled.div`
  font-size: ${FONT_SIZES.title5};
  position: absolute;
  top: 6px;
  left: 90px;
  word-break: keep-all;
  display: flex;
  align-items: center;
`;

const JudgementLabel = styled.div`
  margin-right: 19px;
  display: flex;
  align-items: center;
`;

type JudgementStatusProps = {
  $status?: number;
};

const JudgementStatus = styled.div<JudgementStatusProps>`
  display: inline-block;
  color: ${PALETTE.white};
  background-color: ${({ $status }) =>
    $status && (JUDGEMENT_STATUS_COLORS as Record<number, string>)[$status]};
  min-width: 26px;
  min-height: 16px;
  line-height: 16px;
  text-align: center;
  margin-left: 7px;
`;

const User = styled(Typography)`
  margin-top: 14px;
`;

type ListItemTitleProps = {
  $isAdmin?: boolean;
};

const ListItemTitle = styled(Typography)<ListItemTitleProps>`
  padding: 0 53px 0 0;
  margin-top: ${({ $isAdmin }) => ($isAdmin ? '8px' : '26px')};
`;

type Props = {
  survey: {
    id: number;
    surveyStatusName: string;
    surveyStatusId: SurveyStatus;
    userCompanyName?: string;
    name?: string;
    numberOfQuestions?: number;
    created: string;
    modified: string;
    userName?: string;
    userCategory: UserCategory;
    moResearchId?: number;
    antisocialCheckStatus: number;
    antisocialCheckApprovalStatus: number;
    creditCheckStatus?: number;
  };
  isAdmin: boolean;
};

const SurveyListItemBox: React.FC<Props> = ({ survey, isAdmin = false }): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const surveyUrl = ROUTES[isAdmin ? 'ADMIN' : 'USER'].SURVEY.replace(':id', survey.id.toString());

  return (
    <StyledListItem
      data-testid={`uiParts-SurveyListItemBox-${survey.id}`}
      disablePadding
      onClick={() => navigate(surveyUrl)}
    >
      <StatusBoxContainer>
        <StatusBox label={survey.surveyStatusName} status={survey.surveyStatusId} />
        {isAdmin && survey.surveyStatusId === SURVEY_STATUSES.UNDER_REVIEW && (
          <JudgementStatuses>
            <JudgementLabel>
              <Typography variant="title5" component="div">
                {t('myPage.list.antiSocialCheckTitle')}
              </Typography>
              <JudgementStatus
                $status={survey.antisocialCheckStatus}
                data-testid="antisocialCheckStatus"
              >
                {(ANTISOCIAL_CHECK_NAME as Record<number, string>)[survey.antisocialCheckStatus]}
              </JudgementStatus>
            </JudgementLabel>
            <JudgementLabel>
              <Typography variant="title5" component="div">
                {t('myPage.list.antisocialCheckApprovalTitle')}
              </Typography>
              {/* 反社NGの場合、承認ステータスに関わらず"不"に差し替え */}
              {survey.antisocialCheckStatus === JUDGEMENT_STATUS.NG ? (
                <JudgementStatus
                  $status={JUDGEMENT_STATUS.UNNEEDED}
                  data-testid="antisocialCheckApprovalStatus"
                >
                  {ANTISOCIAL_CHECK_APPROVAL_NAME[JUDGEMENT_STATUS.UNNEEDED]}
                </JudgementStatus>
              ) : (
                <JudgementStatus
                  $status={survey.antisocialCheckApprovalStatus}
                  data-testid="antisocialCheckApprovalStatus"
                >
                  {
                    (ANTISOCIAL_CHECK_APPROVAL_NAME as Record<number, string>)[
                      survey.antisocialCheckApprovalStatus
                    ]
                  }
                </JudgementStatus>
              )}
            </JudgementLabel>
            <JudgementLabel>
              <Typography variant="title5" component="div">
                {t('myPage.list.creditCheckTitle')}
              </Typography>
              {survey.creditCheckStatus && (
                <JudgementStatus $status={survey.creditCheckStatus} data-testid="creditCheckStatus">
                  {(CREDIT_CHECK_NAME as Record<number, string>)[survey.creditCheckStatus]}
                </JudgementStatus>
              )}
            </JudgementLabel>
          </JudgementStatuses>
        )}
      </StatusBoxContainer>
      {isAdmin && (
        <User variant="body3">
          <UserCategoryLabel userType={survey.userCategory} />
          {survey.userCategory === USER_CATEGORY.CORPORATE
            ? survey.userCompanyName
            : survey.userName}
        </User>
      )}
      <ListItemTitle variant="body1" $isAdmin={isAdmin}>
        {survey.name}
      </ListItemTitle>
      <ListItemInfo $isAdmin={isAdmin}>
        {t('myPage.numberOfQuestions')}
        <QuestionNum data-testid="questionNum">{survey.numberOfQuestions}</QuestionNum>
        {t('myPage.surveyId')}
        <Id data-testid="surveyId">{survey.id}</Id>
        {t('myPage.created')}
        <CreatedDate data-testid="created">
          {format(parseISO(survey.created), 'yyyy/MM/dd')}
        </CreatedDate>
        {t('myPage.modified')}
        <Modified data-testid="modified">
          {format(parseISO(survey.modified), 'yyyy/MM/dd')}
        </Modified>
        {survey.moResearchId && isAdmin ? (
          <>
            {t('myPage.moResearchId')}
            <Id data-testid="moResearchId">{survey.moResearchId}</Id>
          </>
        ) : null}
      </ListItemInfo>
    </StyledListItem>
  );
};

export default SurveyListItemBox;
